import type { VariantProps } from 'class-variance-authority'
import type { ComponentProps, ReactNode } from 'react'
import { useId } from 'react'

import Reveal from '@/components/motion/Reveal'
import { fieldVariants } from '@/components/ui/field-variants'
import { cn } from '@/lib/utils'

interface FieldProps
  extends
    Omit<ComponentProps<'input'>, 'size'>,
    Omit<VariantProps<typeof fieldVariants>, 'invalid'> {
  /** Visible label above the box. Omit it only when passing an `aria-label`. */
  label?: string
  /** The message itself, not a flag — an invalid field must say what is wrong. */
  error?: string | undefined
  /** Inside the box, before the input: a country dial code, a currency mark. */
  leading?: ReactNode
  /** Inside the box, after the input: a reveal toggle, a unit, a clear button. */
  trailing?: ReactNode
  /** Applied to the box, which is the element that carries the visual shape. */
  className?: string
}

/**
 * The system's text input.
 *
 * The `<input>` is transparent and the box around it is a plain element, so a
 * leading or trailing adornment is a flex sibling rather than an icon
 * absolutely positioned over reserved padding. That matters for the phone
 * prefix, whose width changes with the dial code — padding compensation would
 * have to be measured, and an interactive trailing adornment sitting on top of
 * the input is one z-index mistake away from swallowing every click meant for
 * the text.
 *
 * Below `lg` the text is `body-l` (16px) rather than the frames' `body-m`
 * (14px). iOS Safari zooms the whole page when a focused control computes under
 * 16px and does not zoom back out, which strands the member on a page wider
 * than the viewport. Both sizes are real rungs on the ramp, so this is a
 * breakpoint swap, not a new size.
 *
 * Error text is `destructive`, not `market-down`: red here means "this is
 * wrong", and the market colours are reserved for direction.
 *
 * The label is V2's (`4243:89064`): 12px in the secondary grey, 8px above
 * the box, at 600 as the reference draws it. The placeholder takes the grey a step below it, as
 * Figma draws every placeholder (`4236:72986`, `4236:76386`).
 */
export default function Field({
  label,
  error,
  leading,
  trailing,
  shape,
  className,
  id,
  required,
  ...props
}: FieldProps) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const errorId = `${fieldId}-error`

  return (
    <div className="w-full">
      {label !== undefined ? (
        <label htmlFor={fieldId} className="mb-2 block text-h-xs text-muted-foreground">
          {label}
          {required === true ? (
            <>
              <span aria-hidden="true" className="ml-1 text-destructive">
                *
              </span>
              <span className="sr-only"> (required)</span>
            </>
          ) : null}
        </label>
      ) : null}

      <div className={cn(fieldVariants({ shape, invalid: error !== undefined }), className)}>
        {leading !== undefined ? (
          <span className="flex shrink-0 items-center gap-2 text-body-l text-muted-foreground lg:text-body-m">
            {leading}
          </span>
        ) : null}

        <input
          {...props}
          id={fieldId}
          required={required}
          aria-invalid={error !== undefined ? true : undefined}
          aria-describedby={error !== undefined ? errorId : props['aria-describedby']}
          className="min-w-0 flex-1 bg-transparent text-body-l text-foreground outline-none placeholder:text-muted-foreground-subtle disabled:cursor-not-allowed lg:text-body-m"
        />

        {trailing !== undefined ? (
          <span className="flex shrink-0 items-center">{trailing}</span>
        ) : null}
      </div>

      {/* `role="alert"` so a failed submit is announced, not merely coloured. */}
      {error !== undefined ? (
        <Reveal
          as="p"
          variant="fade-up"
          id={errorId}
          role="alert"
          className="mt-2 text-body-xs text-destructive"
        >
          {error}
        </Reveal>
      ) : null}
    </div>
  )
}
